"use client";

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { applyChatbotVisibility } from "./chatbotVisibility";
import Kenyt from "./Kenyt";
import Tawk from "./Tawk";
import Freshdesk from "./Freshdesk";
import UpgradChatbot from "./UpgradChatbot";
// import GoogleGtag from "./GoogleGtag";
import RudderStack from "./RudderStack";
// [RUDDERSTACK new] page-level events (page_open / tab_switched) — delete file + mount to remove
import RudderStackPageEvents from "./RudderStackPageEvents";
import MicrosoftUET from "./MicrosoftUET";
import MetaPixel from "./MetaPixel";

interface KenytEventDetail {
  eventType: string;
}

/** DB-backed ids resolved on the server (with NEXT_PUBLIC_* fallback already applied). */
export interface PageScriptConfig {
  kenytBotId?: string | null;
  tawkPropertyId?: string | null;
  tawkWidgetId?: string | null;
  freshdeskToken?: string | null;
  freshdeskHost?: string | null;
  freshdeskWidgetUuid?: string | null;
  upgradChatbotClientKey?: string | null;
  upgradChatbotBotId?: string | null;
  upgradChatbotUrl?: string | null;
  gaId?: string | null;
  adsCode?: string | null;
  gtmId?: string | null;
  metaPixelId?: string | null;
  microsoftUetTagId?: string | null;
  googleAdsConversionId?: string | null;
  rudderStackWriteKey?: string | null;
}

const PageScript = ({ config }: { config?: PageScriptConfig } = {}) => {
  const chatbotProvider = useSelector(
    (state: { globalslice: { university: { chatbot_provider: string } } }) =>
      state.globalslice?.university?.chatbot_provider ?? ""
  );

  /** Page-level switch, hydrated from `page.chatbot_enabled` by `NewPage`. */
  const chatbotEnabledForPage = useSelector(
    (state: { globalslice: { chatbotEnabled?: boolean } }) =>
      state.globalslice?.chatbotEnabled !== false
  );

  /**
   * The chatbot mounts one render late, on purpose. `PageScript` lives in the root
   * layout and renders in the SAME pass as the page below it, before `NewPage`'s effect
   * has put `chatbot_enabled` into the store — so rendering a provider immediately
   * would inject the vendor script on a page that turns out to have the chatbot off.
   * That injection is not reversible: `next/script`'s `lazyOnload` has no unmount
   * cleanup, so an unmount cannot call the pending load back.
   *
   * Deferring to the next commit closes the gap: `NewPage`'s effect runs before this
   * one (children flush first), so by the time this state change re-renders, the
   * selector above reads the resolved value. `DeferUntilInteraction` in the root layout
   * usually delays this component well past that point already — this keeps the gate
   * correct even when it is switched to `eager`. Non-CMS routes never dispatch and keep
   * the store default (on), so their behaviour is unchanged.
   */
  const [chatbotGateResolved, setChatbotGateResolved] = useState(false);
  useEffect(() => setChatbotGateResolved(true), []);

  const chatbotEnabled = chatbotGateResolved && chatbotEnabledForPage;

  /**
   * Soft navigation keeps this component (and any widget a previous page started)
   * mounted, so leaving the provider unmounted is not enough — see `chatbotVisibility`.
   */
  useEffect(() => {
    applyChatbotVisibility(chatbotEnabledForPage);
  }, [chatbotEnabledForPage]);

  useEffect(() => {
    // Also gated on the page switch: the z-index fixer re-polls until the container
    // shows up, which on a chatbot-off page would be a timer that never stops.
    if (!chatbotEnabled || chatbotProvider !== "kenyt") return;

    const adjustChatbotZIndex = () => {
      const chatbotContainer = document.getElementById(
        "kenyt-chatbot-container"
      );
      if (chatbotContainer) {
        chatbotContainer.style.zIndex = "1";
      } else {
        setTimeout(adjustChatbotZIndex, 100);
      }
    };

    adjustChatbotZIndex();

    const handleKenytEvent: EventListener = (event: Event) => {
      const customEvent = event as CustomEvent<KenytEventDetail>;
      const googleAdsConversionId =
        (config?.googleAdsConversionId && config.googleAdsConversionId.trim()) ||
        process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID;
      if (
        (customEvent.detail.eventType === "kenyt_UserMessage" ||
          customEvent.detail.eventType === "kenyt_ContactFormSubmit") &&
        googleAdsConversionId
      ) {
        window.gtag?.("event", "conversion", {
          send_to: googleAdsConversionId,
        });
      }
    };

    window.addEventListener("kenytEvent", handleKenytEvent);

    return () => {
      window.removeEventListener("kenytEvent", handleKenytEvent);
    };
  }, [chatbotEnabled, chatbotProvider]);

  return (
    <>
    {/* These are not used in the project, and facebook is already preconnected in the MetaPixel.tsx*/}
      {/* <link rel="preconnect" href="https://cdnjs.cloudflare.com" />
      <link rel="preconnect" href="http://static.clevertap.com" />
      <link rel="preconnect" href="https://connect.facebook.net" /> */}

      {/* Poppins is loaded via next/font in app/layout.tsx — avoid duplicate CSS + extra font-display work */}

      {chatbotEnabled && chatbotProvider === "kenyt" && (
        <Kenyt botId={config?.kenytBotId} googleAdsConversionId={config?.googleAdsConversionId} />
      )}
      {chatbotEnabled && chatbotProvider === "tawk" && (
        <Tawk
          propertyId={config?.tawkPropertyId}
          widgetId={config?.tawkWidgetId}
          sendTo={config?.adsCode}
        />
      )}
      {chatbotEnabled && chatbotProvider === "freshdesk" && (
        <Freshdesk
          token={config?.freshdeskToken}
          host={config?.freshdeskHost}
          widgetUuid={config?.freshdeskWidgetUuid}
        />
      )}
      {chatbotEnabled && chatbotProvider === "upgrad_chatbot" && (
        <UpgradChatbot
          clientKey={config?.upgradChatbotClientKey}
          chatBotId={config?.upgradChatbotBotId}
          url={config?.upgradChatbotUrl}
        />
      )}
      {/* <GoogleGtag gaId={config?.gaId} adsId={config?.adsCode} gtmId={config?.gtmId} /> */}
      <RudderStack writeKey={config?.rudderStackWriteKey} />
      {/* [RUDDERSTACK new] page_open + tab_switched — reuses the SDK RudderStack inits above */}
      <RudderStackPageEvents />

      <MicrosoftUET uetTagId={config?.microsoftUetTagId} />
      <MetaPixel pixelId={config?.metaPixelId} />
    </>
  );
};

export default PageScript;
